import { makeStyles, mergeClasses, tokens } from "@fluentui/react-components";
import type { CSSProperties, ReactNode } from "react";

/** 목록 표가 가로로 줄어들지 않는 최소 폭. 이름 열만 남은 폭을 흡수한다. */
export const LIST_TABLE_MIN_WIDTH = 920;

const FIXED: Record<string, number> = {
  team: 168,
  employment: 88,
  grade: 100,
  alloc: 84,
  plans: 148,
  rate: 112,
};

/** DataGrid(flex) 열 폭. 이름 열은 최소 220px에서 늘어난다. */
export function listColStyle(columnId: string | number): CSSProperties {
  if (columnId === "name") return { flex: "1 1 220px", minWidth: 220 };
  const px = FIXED[String(columnId)];
  if (!px) return { flex: "1 1 0", minWidth: 0 };
  return { flex: `0 0 ${px}px`, width: px, minWidth: px, maxWidth: px };
}

export function isNumericCol(columnId: string | number): boolean {
  return columnId === "alloc" || columnId === "rate";
}

/**
 * 표 표준. 멤버 목록과 같다.
 * 외곽선은 없다. 헤더는 본문과 같은 13px·같은 글자색이고, 옅은 면과 아래 선으로만 구분한다.
 */
export const useDataTableStyles = makeStyles({
  frame: {
    overflow: "auto",
    minWidth: 0,
  },
  grid: {
    width: "100%",
    minWidth: `${LIST_TABLE_MIN_WIDTH}px`,
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightRegular,
  },
  header: {
    position: "sticky",
    top: 0,
    zIndex: 2,
    backgroundColor: tokens.colorNeutralBackground3,
  },
  /** DataGrid 행은 이미 아래 선이 있다. 셀에 선을 더하면 이름 칸만 높아져 선이 두 겹으로 어긋난다. */
  headerRow: {
    alignItems: "stretch",
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground3,
  },
  row: {
    alignItems: "stretch",
    minHeight: "38px",
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  headerCell: {
    alignItems: "center",
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground1,
    backgroundColor: tokens.colorNeutralBackground3,
    borderBottom: "none",
  },
  cell: {
    alignItems: "center",
    borderBottom: "none",
    minWidth: 0,
  },
  /** 네이티브 `<table>`. 열 폭은 쓰는 쪽에서 정한다. */
  table: {
    width: "100%",
    borderCollapse: "collapse",
    textAlign: "left",
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightRegular,
  },
  /** Fluent `Table` 루트. 컴포넌트가 글자 크기를 정하지 않아 본문 15px을 물려받지 않게 한다. */
  fluent: {
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
  },
  /** Fluent `TableHeaderCell`. 행 선은 끄고 셀 아래에 헤더 선을 둔다. */
  head: {
    position: "sticky",
    top: 0,
    zIndex: 2,
    height: "36px",
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground1,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightRegular,
  },
  /** Fluent 헤더 행의 기본 아래 선. `head` 선과 겹치지 않게 끈다. */
  headRow: {
    borderBottom: "none",
  },
  th: {
    boxSizing: "border-box",
    height: "36px",
    padding: `6px ${tokens.spacingHorizontalS}`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground1,
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightRegular,
    whiteSpace: "nowrap",
    textAlign: "left",
    verticalAlign: "middle",
    position: "sticky",
    top: 0,
    zIndex: 2,
  },
  td: {
    boxSizing: "border-box",
    height: "34px",
    padding: `6px ${tokens.spacingHorizontalS}`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    verticalAlign: "middle",
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightRegular,
  },
  /** `th`보다 뒤에 두어 숫자 열의 오른쪽 정렬이 헤더의 왼쪽 정렬을 이긴다. */
  num: {
    justifyContent: "flex-end",
    textAlign: "right",
    fontVariantNumeric: "tabular-nums",
    fontFeatureSettings: '"tnum"',
    whiteSpace: "nowrap",
  },
  total: {
    backgroundColor: tokens.colorNeutralBackground3,
  },
  twoLine: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    minWidth: 0,
    maxWidth: "100%",
    padding: `${tokens.spacingVerticalXXS} 0`,
  },
  /** 월 M/M처럼 셀을 눌러 고치는 값. 히트맵 위를 배경색으로 덮지 않는다. */
  editCell: {
    cursor: "pointer",
    ":hover": {
      outline: `1px solid ${tokens.colorNeutralStroke1}`,
      outlineOffset: "-1px",
    },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "-2px",
    },
  },
  editCellOverridden: {
    boxShadow: `inset 0 -2px 0 ${tokens.colorBrandStroke1}`,
  },
  editCellShownHover: {
    outline: `1px solid ${tokens.colorNeutralStroke1}`,
    outlineOffset: "-1px",
  },
  editCellShownFocus: {
    outline: `2px solid ${tokens.colorStrokeFocus2}`,
    outlineOffset: "-2px",
  },
  numLabel: {
    display: "block",
    width: "100%",
    textAlign: "right",
    fontVariantNumeric: "tabular-nums",
  },
  clip: {
    display: "block",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    minWidth: 0,
    maxWidth: "100%",
  },
  /** 이름 13px + 역할 캡션 + 28px 아바타. 목록 DataGrid와 매트릭스 표가 같은 칸을 쓴다. */
  identityCell: {
    height: "44px",
    paddingTop: "4px",
    paddingBottom: "4px",
  },
  identityRow: {
    minHeight: "44px",
  },
  person: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    width: "100%",
    minWidth: 0,
  },
  personText: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    flex: "1 1 auto",
  },
  personName: {
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightRegular,
  },
  nameLine: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    minWidth: 0,
    maxWidth: "100%",
    "& > :not(:first-child)": {
      flexShrink: 0,
    },
  },
  role: {
    display: "block",
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    fontWeight: tokens.fontWeightRegular,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: "100%",
  },
  muted: {
    color: tokens.colorNeutralForeground3,
  },
  danger: {
    color: tokens.colorPaletteRedForeground1,
  },
  allocValue: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: "4px",
    lineHeight: 1,
    "& svg": { display: "block" },
  },
  inactive: {
    opacity: 0.5,
  },
  groupRow: {
    alignItems: "stretch",
    backgroundColor: tokens.colorNeutralBackground3,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    cursor: "pointer",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground3Hover,
    },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "-2px",
    },
  },
  groupCell: {
    alignItems: "center",
    flex: "1 1 auto",
    width: "100%",
    minWidth: 0,
    borderBottom: "none",
    backgroundColor: "transparent",
  },
  groupInner: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXS,
    minWidth: 0,
    width: "100%",
    padding: `0 ${tokens.spacingHorizontalXS}`,
  },
  groupLabel: {
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  dot: {
    width: "8px",
    height: "8px",
    borderRadius: tokens.borderRadiusCircular,
    flexShrink: 0,
  },
  linkBtn: {
    border: "none",
    backgroundColor: "transparent",
    padding: 0,
    margin: 0,
    maxWidth: "100%",
    cursor: "pointer",
    color: tokens.colorNeutralForeground1,
    font: "inherit",
    fontWeight: tokens.fontWeightRegular,
    textAlign: "left",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    borderRadius: tokens.borderRadiusMedium,
    ":hover": { color: tokens.colorNeutralForeground1Hover },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "1px",
    },
  },
  planList: {
    display: "flex",
    flexDirection: "column",
    minWidth: "220px",
    maxWidth: "320px",
    padding: tokens.spacingVerticalXS,
  },
  planItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: tokens.spacingHorizontalS,
    width: "100%",
    border: "none",
    backgroundColor: "transparent",
    cursor: "pointer",
    color: tokens.colorNeutralForeground1,
    font: "inherit",
    fontWeight: tokens.fontWeightRegular,
    textAlign: "left",
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusMedium,
    ":hover": { backgroundColor: tokens.colorNeutralBackground1Hover },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "-2px",
    },
  },
});

export function DataTableFrame({ children, className }: { children: ReactNode; className?: string }) {
  const styles = useDataTableStyles();
  return <div className={mergeClasses(styles.frame, className)}>{children}</div>;
}

const useCellLabelStyles = makeStyles({
  label: {
    color: tokens.colorNeutralForeground1,
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightRegular,
    whiteSpace: "nowrap",
  },
  muted: {
    color: tokens.colorNeutralForeground3,
  },
});

export type CellTone = "default" | "muted";

/** 표 칸의 상태·심각도·우선순위. 색 면 배지 대신 본문과 같은 글자. 끝 상태와 낮음만 옅게 둔다. */
export function CellLabel({ tone = "default", children }: { tone?: CellTone; children: ReactNode }) {
  const styles = useCellLabelStyles();
  return <span className={mergeClasses(styles.label, tone === "muted" && styles.muted)}>{children}</span>;
}
